import clsx from 'clsx';
import { useShallow } from 'zustand/react/shallow';

import { Card } from '#/shared/components/Card';
import { useFeatureFlag } from '#/shared/hooks/useFeatureFlag';
import { usePaidBreakExceptionStore } from '#/shared/stores/paidBreakExceptionStore';
import { useTimesheetStore } from '#/shared/stores/timesheetStore';

import { ShiftTypeLabel } from '../types/types';
import { getShiftDurations } from '../utils/helper';
import { PaidBreakExceptionsActions } from './PaidBreakExceptionsActions';
import { TimeEntryTypePill } from './TimeEntryTypePill';

const commonStyles = 'px-20 py-12 max-tablet-portrait:px-16 border-collapse';

export function TimesheetTotals() {
  const shifts = useTimesheetStore(state => state.timesheet?.shifts ?? []);
  const { isEnabled: paidBreakExceptionsIsEnabled } = useFeatureFlag('PPL-2119-paid-break-exception');
  const totalBreakExceptionsDuration = usePaidBreakExceptionStore(useShallow(s => s.getTotalExceptionsBreak()));
  const totalAcceptedBreakDuration = usePaidBreakExceptionStore(useShallow(s => s.getAcceptedExceptionsBreak()));
  const isBreakExceptionActionsVisible = paidBreakExceptionsIsEnabled && !totalBreakExceptionsDuration.isZero();

  const totalDurations = getShiftDurations(shifts, totalAcceptedBreakDuration);

  return (
    <Card className="max-tablet-portrait:border-0 max-tablet-portrait:rounded-none !p-0">
      <div className="max-tablet-portrait:rounded-none max-tablet-portrait:-ml-16 max-tablet-portrait:-mr-16">
        <table className="w-full rounded-md max-tablet-portrait:rounded-none">
          <thead>
            <tr>
              <th
                colSpan={4}
                className={clsx(
                  commonStyles,
                  'bg-surface-l3',
                  'text-left',
                  'rounded-t-md',
                  'max-tablet-portrait:rounded-none',
                  'text-18',
                )}
              >
                Total hours
              </th>
            </tr>
          </thead>
          <tbody className="border-collapse">
            {/* Spacer row for the first shift type */}
            <tr aria-hidden="true">
              <td colSpan={2} className="h-8 bg-surface-l1" />
            </tr>
            {renderShiftTypeRow('Ordinary', totalDurations['Ordinary'])}
            {renderShiftTypeRow('On-call', totalDurations['On-Call'])}
            {renderShiftTypeRow('Callback', totalDurations['Callback'])}
            {renderBreakRow(totalDurations.break, !isBreakExceptionActionsVisible)}

            {isBreakExceptionActionsVisible && (
              <tr>
                <td
                  colSpan={2}
                  className={clsx(
                    commonStyles,
                    'bg-surface-l1 pt-0 pb-16 rounded-bl-md rounded-br-md max-tablet-portrait:rounded-none',
                  )}
                >
                  <div className="hidden tablet-portrait:block">
                    <PaidBreakExceptionsActions />
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );

  function renderShiftTypeRow(type: ShiftTypeLabel, duration: string) {
    return (
      <tr>
        <td className={clsx(commonStyles, 'bg-surface-l1')}>
          <TimeEntryTypePill type={type} />
        </td>
        <td className={clsx(commonStyles, 'bg-surface-l1', 'text-right text-18')}>{duration}</td>
      </tr>
    );
  }

  function renderBreakRow(duration: string, lastRow = false) {
    return (
      <tr>
        <td
          className={clsx(commonStyles, 'bg-surface-l1', lastRow && 'rounded-bl-md max-tablet-portrait:rounded-none')}
        >
          Break (unpaid)
        </td>
        <td
          className={clsx(
            commonStyles,
            'bg-surface-l1',
            'text-right text-18',
            lastRow && 'rounded-br-md max-tablet-portrait:rounded-none',
          )}
        >
          {duration}
        </td>
      </tr>
    );
  }
}
